import { PaymentMethodType } from "@/common/types/PaymentMethod.types";
import {
  PaymentRequestStatus,
  PaymentRequestTypeProviders
} from "@/common/types/PaymentRequestTypes";

type LineAdditionalServicesChipNotes =
  LineAdditionalServicesPhysicalChipNotes | LineAdditionalServicesESimNotes;

export type LineAdditionalServicesNotes =
  LineAdditionalServicesChipNotes | LineAdditionalServicesNotesDetails;

export enum LineAdditionalServiceType {
  DATA_BONUS = "DATA_BONUS",
  PHYSICAL_CHIP = "PHYSICAL_CHIP",
  ESIM = "ESIM"
}

export enum LineAdditionalServiceCategory {
  ACTIVATION_C = "ACTIVATION_C",
  DATA_BONUS = "DATA_BONUS"
}

export enum LineAdditionalServiceUnit {
  UNIT = "UNIT",
  GB = "GB"
}

export enum LineAdditionalServiceStepName {
  CREATED = "CREATED",
  CANCELED = "CANCELED",
  DELIVERED = "DELIVERED",
  PAID = "PAID"
}

export enum LineAdditionalServiceInteractionAction {
  CANCEL = "cancel",
  CONFIRM_PAYMENT = "confirm-payment",
  DELIVER_SERVICE = "deliver-service"
}

export interface LineAdditionalServiceStep {
  id: number;
  order: number;
  internalName: LineAdditionalServiceStepName;
  friendlyName: string;
  interaction: boolean;
  interactionType?: string;
  interactionAction?: LineAdditionalServiceInteractionAction;
  interactionName?: string;
}

export interface LineAdditionalServicePaymentRequest {
  id: number;
  transactionId?: string;
  typeProvider: PaymentRequestTypeProviders;
  code: string;
  requestStatus: PaymentRequestStatus;
  expiresAt: Date;
  completedAt?: Date;
  refundedAt?: Date;
}

interface LineAdditionalServiceBase {
  id: number;
  lineId: number;
  contractId: number;
  requestedByUserId: number;
  quantity: number;
  unit: LineAdditionalServiceUnit;
  value: number;
  approvalFlowId: number;
  currentStepId: number;
  nextStepId?: number;
  createdAt: Date;
  paymentConfirmedByUserId?: number;
  deliveryConfirmedByUserId?: number;
  deliveredAt?: Date;
  canceledByUserId?: number;
  paymentRequest?: LineAdditionalServicePaymentRequest;
}

export interface LineAdditionalServiceDataBonus extends LineAdditionalServiceBase {
  type: LineAdditionalServiceType.DATA_BONUS;
  notes?: LineAdditionalServicesNotesDetails;
}

export interface LineAdditionalServicePhysicalChip extends LineAdditionalServiceBase {
  type: LineAdditionalServiceType.PHYSICAL_CHIP;
  notes?: LineAdditionalServicesPhysicalChipNotes;
}

export interface LineAdditionalServiceESim extends LineAdditionalServiceBase {
  type: LineAdditionalServiceType.ESIM;
  notes?: LineAdditionalServicesESimNotes;
}

export type LineAdditionalService =
  | LineAdditionalServiceDataBonus
  | LineAdditionalServicePhysicalChip
  | LineAdditionalServiceESim;

export type LineAdditionalServiceListItem = LineAdditionalService & {
  currentStep: LineAdditionalServiceStep;
  nextStep?: LineAdditionalServiceStep;
  requestedByUser: {
    id: number;
    fullName: string;
  };
};

export interface LineAdditionalServiceFormValues {
  type: LineAdditionalServiceType;
  quantity: number;
  unit: LineAdditionalServiceUnit;
  value: number;
  paymentMethod?: PaymentMethodType.INVOICE_DEBIT | PaymentMethodType.PIX;
  notes?: LineAdditionalServicesNotes;
}

export interface CreateLineAdditionalServicePayload extends LineAdditionalServiceFormValues {
  lineId: number;
  contractId: number;
}

export interface PhysicalChipDeliveryFormValues {
  iccid: string;
  deliveryByMail: boolean;
  trackingCode: string;
  rescueProtocol: string;
}

export interface ProcessLineAdditionalServiceStepPayload {
  interactionAction: LineAdditionalServiceInteractionAction;
  iccid?: string;
  trackingCode?: string;
  rescueProtocol?: string;
}

export interface LineAdditionalServicesNotesFormValues {
  details?: string;
  deliveryAddress: ILineAdditionalServicesNotesDeliveryAddress;
  eSimInfo: ILineAdditionalServicesNotesESimInfo;
}

export interface ILineAdditionalServicesNotesDeliveryAddress {
  zipCode: string;
  city: string;
  state: string;
  street: string;
  neighborhood: string;
  number?: string;
  note?: string;
}

interface ILineAdditionalServicesNotesESimInfo {
  IMEI: string;
  EID: string;
  deviceModel: string;
}

interface LineAdditionalServicesNotesDetails {
  details?: string;
}

interface LineAdditionalServicesPhysicalChipNotes extends LineAdditionalServicesNotesDetails {
  deliveryAddress: ILineAdditionalServicesNotesDeliveryAddress;
  iccid?: string;
  trackingCode?: string;
  rescueProtocol?: string;
}

interface LineAdditionalServicesESimNotes extends LineAdditionalServicesNotesDetails {
  eSimInfo: ILineAdditionalServicesNotesESimInfo;
}
